import { Children, cloneElement, forwardRef, isValidElement } from "react";
import classNames from "classnames";
import Icon from "@/components/Icon";
import type { MaterialNames } from "@/components/material_symbol_names";
import "./Snackbar.css";

const actionLabelClassName = "underline underline-offset-2";

function withUnderlinedActionLabels(actions: React.ReactNode) {
  return Children.map(actions, (action) => {
    if (!isValidElement<{ children?: React.ReactNode }>(action)) {
      return action;
    }

    return cloneElement(action, undefined, <span className={actionLabelClassName}>{action.props.children}</span>);
  });
}

export type SnackbarVariant = "success" | "error" | "warning" | "info";

export interface SnackbarProps {
  message: string;
  subtitle?: string;
  variant?: SnackbarVariant;
  actions?: React.ReactNode;
  close?: boolean;
  onClose?: () => void;
}

const variantConfig: Record<SnackbarVariant, { icon: MaterialNames; chipClassName: string; iconClassName: string }> = {
  success: {
    icon: "check_circle",
    chipClassName: "bg-extended-success-brand-color",
    iconClassName: "text-extended-success-brand-on-color"
  },
  error: {
    icon: "error",
    chipClassName: "bg-sys-brand-error",
    iconClassName: "text-sys-brand-on-error"
  },
  warning: {
    icon: "warning",
    chipClassName: "bg-extended-warning-brand-color",
    iconClassName: "text-extended-warning-brand-on-color"
  },
  info: {
    icon: "info",
    chipClassName: "bg-sys-brand-primary",
    iconClassName: "text-sys-brand-on-primary"
  }
};

export const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(function Snackbar(
  { message, subtitle, variant = "success", actions, close = true, onClose },
  ref
) {
  const { icon, chipClassName, iconClassName } = variantConfig[variant];
  const isAlert = variant === "error" || variant === "warning";
  const isSingleLine = !subtitle && !actions;

  return (
    <div
      ref={ref}
      className="snackbar"
      data-testid="snackbar"
      data-layout={isSingleLine ? "single" : "stack"}
      data-close={close ? "true" : "false"}
      role={isAlert ? "alert" : "status"}
    >
      <div className={classNames("snackbar__icon", chipClassName)} data-testid="snackbar-icon" aria-hidden="true">
        <Icon name={icon} size={24} filled className={iconClassName} />
      </div>
      <div className="snackbar__copy">
        <p className="snackbar__headline text-body-large-bold text-sys-brand-on-surface">{message}</p>
        {subtitle && (
          <p className="snackbar__subtitle text-body-medium text-sys-brand-on-surface-variant">{subtitle}</p>
        )}
        {actions && (
          <div className="snackbar__actions" data-testid="snackbar-actions">
            {withUnderlinedActionLabels(actions)}
          </div>
        )}
      </div>
      {close && (
        <button
          type="button"
          aria-label="Dismiss"
          className="snackbar__close"
          data-testid="close-snackbar"
          onClick={onClose}
        >
          <span aria-hidden="true">
            <Icon name="close" size={20} />
          </span>
        </button>
      )}
    </div>
  );
});
